import { FileDropZone } from 'dropzone-compat';
import * as React from 'react';
import PropTypes from 'prop-types';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import TableBody from '@mui/material/TableBody';
import Grid from '@mui/material/Grid';
import notiStack from '../../common/notiStack';
import { useCommonStyles } from './Styles';
import { hrFileSize } from '../../common/helpers';
import TableButton from './buttons/TableButton';
import './FileUpload.css';
import { UploadButton } from './buttons/BaseButton';
import {mimeTypes} from "../../common/constants.js";

const FILE_UPLOAD_IMAGE_MIME_TYPES = [
  'image/svg+xml',
  'image/jpeg',
  'image/png',
  'image/gif',
];

const DEFAULT_FILE_UPLOAD_ACCEPTED_MIME_TYPES = [
  'text/plain',
  'text/html',
  'application/pdf',
  'application/msword',
  'text/csv',
  ...FILE_UPLOAD_IMAGE_MIME_TYPES,
  ...mimeTypes.excel,
];

function FileUpload({
  acceptedMimeTypes = DEFAULT_FILE_UPLOAD_ACCEPTED_MIME_TYPES,
  onChange,
  singleFile = false,
  displayList = true,
  onUploadPromise = null,
  largeButton = false,
  xs
}) {
  const classes = useCommonStyles();
  const [files, setFiles] = React.useState([]);

  const onAddUploadFile = (newFiles) => {
    if (singleFile) {
      setFiles(newFiles);
      if (onChange) onChange(newFiles);
    } else {
      const mergedFiles = [...files, ...newFiles];
      setFiles(mergedFiles);
      if (onChange) onChange(mergedFiles);
    }
  };

  const onRejectUploadFile = (rejFiles) => {
    rejFiles.forEach((file) => {
      notiStack.error(`Failed to load file ${file}`);
    });
  };

  const renderAttachingFileList = () => (
    <Grid
      size={{
        xs: xs,
        xl: 12
      }}>
      <Paper>
        <div className={classes.tableHolderRootNoFooter}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>File Name</TableCell>
                <TableCell>Size</TableCell>
                <TableCell>&nbsp;</TableCell>
              </TableRow>
            </TableHead>
            <TableBody key={files.length}>
              {
                files.map(
                  (attachedFile, i) => (
                    <TableRow key={attachedFile.name}>
                      <TableCell>{attachedFile.name}</TableCell>
                      <TableCell>{hrFileSize(attachedFile.size)}</TableCell>
                      <TableCell>
                        <TableButton
                          key={attachedFile.name}
                          color="secondary"
                          onClick={() => {
                            const newFiles = [...files];
                            newFiles.splice(i, 1);
                            setFiles(newFiles);
                            if (onChange) onChange(newFiles);
                          }}
                        >
                          Remove
                        </TableButton>
                      </TableCell>
                    </TableRow>
                  ),
                )
              }
            </TableBody>
          </Table>
        </div>
      </Paper>
    </Grid>
  );

  return (
    <Grid container spacing={2}>
      <Grid size={12}>
        <FileDropZone
          acceptedMimeTypes={acceptedMimeTypes}
          onFilesAdded={onAddUploadFile}
          onFilesRejected={onRejectUploadFile}
          elevation={2}
          dragOverElevation={10}
          className="file-drop-zone"
        />
      </Grid>
      {
        files.length > 0 && displayList ? renderAttachingFileList() : ''
      }
      {
        onUploadPromise && Object.values(files).length > 0 && (
          <Grid size={12}>
            <Grid container spacing={1} justifyContent="flex-end">
              {
                largeButton ? (
                  <UploadButton
                    key={files}
                    color="primary"
                    buttonText="upload"
                    onClickPromise={() => new Promise((resolve, reject) => {
                      const fn = onUploadPromise(files);
                      fn.then((value) => {
                        const ret = resolve(value);
                        setFiles([]);
                        return ret;
                      }, reason => reject(reason)).then(error => reject(error));
                    })}
                  />
                ) : (
                  <TableButton
                    key={files}
                    color="primary"
                    buttonText="upload"
                    onClickPromise={() => new Promise((resolve, reject) => {
                      const fn = onUploadPromise();
                      fn.then((value) => {
                        const ret = resolve(value);
                        setFiles([]);
                        return ret;
                      }, reason => reject(reason)).then(error => reject(error));
                    })}
                  />
                )
              }

            </Grid>
          </Grid>
        )
      }
    </Grid>
  );
}

FileUpload.imageMimeTypes = FILE_UPLOAD_IMAGE_MIME_TYPES;

FileUpload.propTypes = {
  acceptedMimeTypes: PropTypes.arrayOf(PropTypes.string),
  singleFile: PropTypes.bool,
  displayList: PropTypes.bool,
  onUploadPromise: PropTypes.func,
  largeButton: PropTypes.bool,
};

export default FileUpload;
